<template>
  <div class="invoice-detail-container">
    <!-- Loading -->
    <div v-if="loading" class="loading-container">
      <div class="skeleton-header"></div>
      <div class="skeleton-body"></div>
    </div>

    <template v-else-if="invoice">
      <!-- Header -->
      <div class="detail-header">
        <div class="header-left">
          <button class="back-btn" @click="$router.push('/invoicing')">
            <i class="fas fa-arrow-left"></i>
          </button>
          <div>
            <div class="invoice-title-row">
              <h1 class="page-title">{{ invoice.invoice_number }}</h1>
              <span class="status-badge" :class="`status-${invoice.status}`">{{ invoice.status }}</span>
            </div>
            <p class="page-subtitle">{{ invoice.client_organization_name }}</p>
          </div>
        </div>
        <div class="header-actions">
          <button class="btn btn-secondary" @click="$router.push(`/invoicing/${invoice.id}/edit`)">
            <i class="fas fa-edit"></i>
            Edit
          </button>
          <button
            class="btn btn-secondary"
            @click="generatePDF"
            :disabled="generatingPDF"
          >
            <i :class="generatingPDF ? 'fas fa-spinner fa-spin' : 'fas fa-file-pdf'"></i>
            {{ generatingPDF ? 'Generating...' : 'Generate PDF' }}
          </button>
          <button
            v-if="invoice.has_pdf"
            class="btn btn-primary"
            @click="downloadPDF"
          >
            <i class="fas fa-download"></i>
            Download PDF
          </button>
          <button
            v-if="hasSourceDocuments || invoice.has_pdf"
            class="btn btn-secondary"
            @click="downloadPack('zip')"
          >
            <i class="fas fa-file-archive"></i>
            Invoice + receipts (ZIP)
          </button>
          <button
            v-if="hasSourceDocuments && invoice.has_pdf"
            class="btn btn-secondary"
            @click="downloadPack('merged')"
          >
            <i class="fas fa-copy"></i>
            Merged PDF
          </button>
          <button
            v-if="invoice.status === 'DRAFT'"
            class="btn btn-action"
            @click="markSent"
          >
            <i class="fas fa-paper-plane"></i>
            Mark Sent
          </button>
          <button
            v-if="invoice.status === 'SENT'"
            class="btn btn-success"
            @click="markPaid"
          >
            <i class="fas fa-check-circle"></i>
            Mark Paid
          </button>
          <button
            v-if="['DRAFT', 'SENT'].includes(invoice.status)"
            class="btn btn-danger"
            @click="voidInvoice"
          >
            <i class="fas fa-ban"></i>
            Void
          </button>
        </div>
      </div>

      <!-- Invoice Body -->
      <div class="invoice-body">

        <!-- Left: Invoice Preview Card -->
        <div class="invoice-preview-card">

          <!-- Invoice Header -->
          <div class="inv-header">
            <div class="issuer-info">
              <h2 class="issuer-name">{{ issuerName }}</h2>
              <p class="issuer-contact" v-if="invoice.from_email">{{ invoice.from_email }}</p>
              <p class="issuer-contact" v-if="invoice.from_address">{{ invoice.from_address }}</p>
            </div>
            <div class="inv-meta">
              <div class="inv-label">Invoice</div>
              <div class="inv-number">{{ invoice.invoice_number }}</div>
            </div>
          </div>

          <!-- Parties -->
          <div class="parties-row">
            <div class="party">
              <div class="party-label">Bill To</div>
              <div class="party-name">{{ invoice.client_organization_name }}</div>
              <div class="party-email" v-if="invoice.client_organization_email">
                {{ invoice.client_organization_email }}
              </div>
            </div>
            <div class="party">
              <div class="party-label">Dates</div>
              <div class="date-item">Issue: <strong>{{ formatDate(invoice.issue_date) }}</strong></div>
              <div class="date-item" v-if="invoice.due_date">Due: <strong>{{ formatDate(invoice.due_date) }}</strong></div>
              <div class="date-item" v-if="invoice.billing_period_start">
                Period: <strong>{{ formatDate(invoice.billing_period_start) }} – {{ formatDate(invoice.billing_period_end) }}</strong>
              </div>
            </div>
          </div>

          <!-- Line Items Table -->
          <table class="line-items-table">
            <thead>
              <tr>
                <th>Description</th>
                <th class="right">Qty</th>
                <th class="right">Unit Price</th>
                <th class="right">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in invoice.line_items" :key="item.id">
                <td>
                  <span class="type-badge" :class="`badge-${item.line_type}`">
                    {{ lineTypeLabel(item.line_type) }}
                  </span>
                  {{ item.description }}
                </td>
                <td class="right">{{ parseFloat(item.quantity).toFixed(2) }}</td>
                <td class="right">${{ parseFloat(item.unit_price).toFixed(2) }}</td>
                <td class="right"><strong>${{ parseFloat(item.amount).toFixed(2) }}</strong></td>
              </tr>
              <tr v-if="invoice.line_items.length === 0">
                <td colspan="4" class="no-items">No line items.</td>
              </tr>
            </tbody>
          </table>

          <!-- Totals -->
          <div class="totals-block">
            <div class="totals-row">
              <span>Subtotal</span>
              <span>${{ parseFloat(invoice.subtotal).toFixed(2) }}</span>
            </div>
            <div class="totals-row" v-if="parseFloat(invoice.tax_rate) > 0">
              <span>Tax ({{ (parseFloat(invoice.tax_rate) * 100).toFixed(1) }}%)</span>
              <span>${{ parseFloat(invoice.tax_amount).toFixed(2) }}</span>
            </div>
            <div class="totals-row grand-total">
              <span>Total Due</span>
              <strong>{{ invoice.currency }} ${{ parseFloat(invoice.total).toFixed(2) }}</strong>
            </div>
          </div>

          <!-- Notes -->
          <div class="notes-block" v-if="invoice.notes">
            <div class="notes-label">Notes</div>
            <p>{{ invoice.notes }}</p>
          </div>

          <div v-if="hasSourceDocuments" class="source-docs-block">
            <div class="notes-label">Paid invoices attached (pass-through backup)</div>
            <ul class="source-doc-list">
              <li v-for="doc in invoice.source_documents" :key="doc.id" class="source-doc-row">
                <span>{{ doc.vendor || doc.original_filename }}</span>
                <span v-if="doc.amount_usd" class="source-amt">${{ doc.amount_usd }}</span>
                <button type="button" class="link-btn" @click="downloadSourceDoc(doc)">
                  Download copy
                </button>
              </li>
            </ul>
          </div>
        </div>

        <!-- Right: GitHub Commit Appendix -->
        <div class="sidebar">
          <!-- Summary Card -->
          <div class="summary-card">
            <h3 class="summary-title">Invoice Summary</h3>
            <div class="summary-row">
              <span class="summary-label">Status</span>
              <span class="status-badge" :class="`status-${invoice.status}`">{{ invoice.status }}</span>
            </div>
            <div class="summary-row">
              <span class="summary-label">Total</span>
              <span class="summary-value-big">{{ invoice.currency }} ${{ parseFloat(invoice.total).toFixed(2) }}</span>
            </div>
            <div class="summary-row">
              <span class="summary-label">Line Items</span>
              <span class="summary-value">{{ invoice.line_items.length }}</span>
            </div>
            <div class="summary-row" v-if="invoice.pdf_generated_at">
              <span class="summary-label">PDF Generated</span>
              <span class="summary-value">{{ formatDate(invoice.pdf_generated_at) }}</span>
            </div>
            <div class="summary-row" v-if="invoice.paid_at">
              <span class="summary-label">Paid On</span>
              <span class="summary-value paid-value">{{ formatDate(invoice.paid_at) }}</span>
            </div>
          </div>

          <!-- Line Items By Type -->
          <div class="breakdown-card">
            <h3 class="summary-title">Breakdown by Type</h3>
            <div v-for="(items, type) in groupedByType" :key="type" class="breakdown-row">
              <div class="breakdown-type">
                <span class="type-badge" :class="`badge-${type}`">{{ lineTypeLabel(type) }}</span>
                <span class="breakdown-count">{{ items.length }}</span>
              </div>
              <span class="breakdown-amount">${{ itemsTotal(items).toFixed(2) }}</span>
            </div>
          </div>

          <!-- GitHub Commits List (if any) -->
          <div class="commits-appendix" v-if="githubItems.length > 0">
            <h3 class="summary-title"><i class="fab fa-github"></i> GitHub Activity ({{ githubItems.length }} commits)</h3>
            <div v-for="item in githubItems" :key="item.id" class="commit-row">
              <div class="commit-top-row">
                <a
                  v-if="item.commit_url"
                  :href="item.commit_url"
                  target="_blank"
                  class="commit-sha-link"
                >{{ item.github_commit_sha?.substring(0, 7) }}</a>
                <span v-else class="commit-sha-text">{{ item.github_commit_sha?.substring(0, 7) }}</span>
                <span class="commit-repo-label">{{ item.github_repo }}</span>
                <span class="commit-date-label" v-if="item.commit_date">{{ formatDateShort(item.commit_date) }}</span>
              </div>
              <div class="commit-message-text">{{ item.description }}</div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <div v-else class="error-state">
      <i class="fas fa-exclamation-triangle"></i>
      <p>Invoice not found.</p>
      <button class="btn btn-primary" @click="$router.push('/invoicing')">Back to Invoices</button>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';

export default {
  name: 'InvoiceDetail',

  data() {
    return {
      invoice: null,
      loading: true,
      generatingPDF: false,
    };
  },

  computed: {
    issuerName() {
      return this.invoice?.from_name
        || this.invoice?.issuer_organization_name
        || '';
    },

    githubItems() {
      return (this.invoice?.line_items || []).filter((i) => i.line_type === 'GITHUB_COMMIT');
    },

    groupedByType() {
      const groups = {};
      (this.invoice?.line_items || []).forEach((item) => {
        if (!groups[item.line_type]) groups[item.line_type] = [];
        groups[item.line_type].push(item);
      });
      return groups;
    },

    hasSourceDocuments() {
      return (this.invoice?.source_documents || []).length > 0;
    },
  },

  async mounted() {
    await this.loadInvoice();
  },

  methods: {
    async loadInvoice() {
      this.loading = true;
      try {
        const res = await api.get(`/invoices/${this.$route.params.id}/`);
        this.invoice = res.data;
      } catch (e) {
        this.invoice = null;
      } finally {
        this.loading = false;
      }
    },

    formatDate(dateStr) {
      if (!dateStr) return '—';
      return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },

    formatDateShort(dateStr) {
      if (!dateStr) return '';
      return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    },

    lineTypeLabel(type) {
      const map = {
        FLAT_FEE: 'Flat Fee', HOURLY: 'Hourly', EXPENSE: 'Expense',
        GITHUB_COMMIT: 'Commit', TOKEN_USAGE: 'Tokens', SUBSCRIPTION: 'Subscription',
      };
      return map[type] || type;
    },

    itemsTotal(items) {
      return items.reduce((s, i) => s + parseFloat(i.amount || 0), 0);
    },

    async generatePDF() {
      this.generatingPDF = true;
      try {
        await api.post(`/invoices/${this.invoice.id}/generate-pdf/`);
        await this.loadInvoice();
      } catch (e) {
        alert('PDF generation failed: ' + (e.response?.data?.detail || e.message));
      } finally {
        this.generatingPDF = false;
      }
    },

    async downloadPDF() {
      try {
        const response = await api.get(`/invoices/${this.invoice.id}/download-pdf/`, {
          responseType: 'blob',
        });
        this.saveBlob(
          response.data,
          `Invoice-${this.invoice.invoice_number || this.invoice.id}.pdf`,
          'application/pdf',
        );
      } catch (e) {
        alert('Download failed: ' + (e.response?.data?.detail || e.message));
      }
    },

    saveBlob(data, filename, mime) {
      const blob = new Blob([data], { type: mime });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },

    async downloadPack(format) {
      try {
        const response = await api.get(
          `/invoices/${this.invoice.id}/download-pack/`,
          { params: { format }, responseType: 'blob' },
        );
        const invNum = this.invoice.invoice_number || this.invoice.id;
        if (format === 'merged') {
          this.saveBlob(response.data, `Invoice-${invNum}-with-receipts.pdf`, 'application/pdf');
        } else {
          this.saveBlob(response.data, `Invoice-${invNum}-with-receipts.zip`, 'application/zip');
        }
      } catch (e) {
        alert('Download failed: ' + (e.response?.data?.detail || e.message));
      }
    },

    async downloadSourceDoc(doc) {
      try {
        const response = await api.get(
          `/invoices/${this.invoice.id}/source-documents/${doc.id}/download/`,
          { responseType: 'blob' },
        );
        this.saveBlob(
          response.data,
          doc.original_filename || `source-${doc.id}`,
          response.headers['content-type'] || 'application/octet-stream',
        );
      } catch (e) {
        alert('Download failed: ' + (e.response?.data?.detail || e.message));
      }
    },

    async markSent() {
      await api.post(`/invoices/${this.invoice.id}/mark-sent/`);
      await this.loadInvoice();
    },

    async markPaid() {
      await api.post(`/invoices/${this.invoice.id}/mark-paid/`);
      await this.loadInvoice();
    },

    async voidInvoice() {
      if (!confirm('Void this invoice? This cannot be undone.')) return;
      await api.post(`/invoices/${this.invoice.id}/void/`);
      await this.loadInvoice();
    },
  },
};
</script>

<style scoped>
.invoice-detail-container {
  padding: 24px;
  max-width: 1300px;
  margin: 0 auto;
}

.loading-container { display: flex; flex-direction: column; gap: 16px; }
.skeleton-header { height: 64px; border-radius: 10px; background: #f0f0f0; animation: shimmer 1.4s infinite; }
.skeleton-body { height: 400px; border-radius: 10px; background: #f0f0f0; animation: shimmer 1.4s infinite; }

@keyframes shimmer {
  0% { background-color: #f0f0f0; }
  50% { background-color: #e0e0e0; }
  100% { background-color: #f0f0f0; }
}

.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 28px;
  gap: 16px;
  flex-wrap: wrap;
}

.header-left { display: flex; align-items: flex-start; gap: 16px; }

.back-btn {
  background: none;
  border: 1px solid var(--border, #e0e4f0);
  border-radius: 8px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--text-secondary, #6c7a9c);
  transition: all 0.15s;
  flex-shrink: 0;
  margin-top: 4px;
}
.back-btn:hover { background: #f0f3ff; }

.invoice-title-row { display: flex; align-items: center; gap: 12px; }
.page-title { font-size: 1.5rem; font-weight: 700; font-family: monospace; color: var(--text-primary, #1a1a2e); }
.page-subtitle { color: var(--text-secondary, #6c7a9c); font-size: 0.875rem; margin-top: 4px; }
.header-actions { display: flex; gap: 8px; flex-wrap: wrap; }

.btn {
  padding: 8px 16px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.8rem;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.15s;
}
.btn:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-primary { background: var(--primary, #2c3e8c); color: #fff; }
.btn-secondary { background: #f0f3ff; color: var(--primary, #2c3e8c); }
.btn-action { background: #e3f2fd; color: #1565c0; }
.btn-success { background: #e8f5e9; color: #2e7d32; }
.btn-danger { background: #fce4ec; color: #c62828; }

.status-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.status-DRAFT   { background: #f0f0f0; color: #666; }
.status-SENT    { background: #e3f2fd; color: #1565c0; }
.status-PAID    { background: #e8f5e9; color: #2e7d32; }
.status-VOID    { background: #fce4ec; color: #c62828; }
.status-OVERDUE { background: #fff3e0; color: #e65100; }

.invoice-body {
  display: grid;
  grid-template-columns: 1fr 320px;
  gap: 24px;
  align-items: start;
}

@media (max-width: 960px) {
  .invoice-body { grid-template-columns: 1fr; }
}

/* Invoice Preview Card */
.invoice-preview-card {
  background: #fff;
  border: 1px solid var(--border, #e0e4f0);
  border-radius: 12px;
  padding: 32px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
}

.inv-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding-bottom: 24px;
  border-bottom: 3px solid var(--primary, #2c3e8c);
  margin-bottom: 24px;
}

.issuer-name { font-size: 1.4rem; font-weight: 700; color: var(--primary, #2c3e8c); margin: 0; }
.issuer-contact { margin: 2px 0 0; font-size: 0.85rem; color: #555; }
.inv-label { font-size: 1.3rem; font-weight: 700; color: var(--primary, #2c3e8c); text-align: right; }
.inv-number { font-size: 0.9rem; font-weight: 600; color: #555; font-family: monospace; text-align: right; }

.parties-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 24px;
}

.party-label {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: var(--primary, #2c3e8c);
  margin-bottom: 6px;
}
.party-name { font-size: 1rem; font-weight: 600; }
.party-email { font-size: 0.8rem; color: var(--text-secondary, #6c7a9c); }
.date-item { font-size: 0.85rem; color: var(--text-secondary, #6c7a9c); margin-bottom: 3px; }

.line-items-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 24px;
  font-size: 0.875rem;
}

.line-items-table thead tr { background: var(--primary, #2c3e8c); }
.line-items-table thead th {
  padding: 10px 12px;
  text-align: left;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.right { text-align: right; }
.line-items-table tbody tr { border-bottom: 1px solid #eef0f5; }
.line-items-table tbody tr:nth-child(even) { background: #fafbff; }
.line-items-table tbody td { padding: 10px 12px; vertical-align: top; }
.no-items { text-align: center; color: var(--text-secondary, #6c7a9c); padding: 24px; }

.type-badge {
  display: inline-block;
  padding: 2px 7px;
  border-radius: 4px;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-right: 4px;
}
.badge-FLAT_FEE      { background: #e8eaf6; color: #3949ab; }
.badge-HOURLY        { background: #e0f2f1; color: #00695c; }
.badge-EXPENSE       { background: #fff8e1; color: #f57f17; }
.badge-GITHUB_COMMIT { background: #f3e5f5; color: #6a1b9a; }
.badge-TOKEN_USAGE   { background: #e3f2fd; color: #1565c0; }
.badge-SUBSCRIPTION  { background: #e8f5e9; color: #2e7d32; }

.totals-block {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  margin-bottom: 24px;
}

.totals-row {
  display: flex;
  gap: 40px;
  font-size: 0.9rem;
  color: var(--text-primary, #1a1a2e);
  min-width: 240px;
  justify-content: space-between;
}

.grand-total {
  font-size: 1.1rem;
  border-top: 2px solid var(--primary, #2c3e8c);
  padding-top: 10px;
  margin-top: 6px;
  font-weight: 700;
}

.notes-block {
  background: #f8f9ff;
  border-left: 4px solid var(--primary, #2c3e8c);
  padding: 12px 16px;
  border-radius: 0 8px 8px 0;
}
.notes-label { font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.8px; color: var(--primary, #2c3e8c); margin-bottom: 4px; }
.notes-block p { font-size: 0.875rem; color: #444; }

.source-docs-block {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e8ecf4;
}

.source-doc-list {
  list-style: none;
  padding: 0;
  margin: 8px 0 0;
}

.source-doc-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  font-size: 0.875rem;
  border-bottom: 1px solid #f0f2f8;
}

.source-amt {
  font-weight: 600;
  margin-left: auto;
}

.link-btn {
  background: none;
  border: none;
  color: #2c3e8c;
  cursor: pointer;
  font-size: 0.8rem;
  text-decoration: underline;
}

/* Sidebar */
.sidebar { display: flex; flex-direction: column; gap: 16px; }

.summary-card,
.breakdown-card,
.commits-appendix {
  background: #fff;
  border: 1px solid var(--border, #e0e4f0);
  border-radius: 12px;
  padding: 20px;
}

.summary-title {
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--text-primary, #1a1a2e);
  margin-bottom: 14px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.summary-title i { color: var(--primary, #2c3e8c); }

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid #f0f0f0;
  font-size: 0.85rem;
}
.summary-row:last-child { border-bottom: none; }
.summary-label { color: var(--text-secondary, #6c7a9c); }
.summary-value { font-weight: 500; }
.summary-value-big { font-size: 1.1rem; font-weight: 700; color: var(--primary, #2c3e8c); }
.paid-value { color: #2e7d32; font-weight: 600; }

.breakdown-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid #f0f0f0;
}
.breakdown-row:last-child { border-bottom: none; }
.breakdown-type { display: flex; align-items: center; gap: 8px; }
.breakdown-count { font-size: 0.75rem; color: var(--text-secondary, #6c7a9c); }
.breakdown-amount { font-weight: 600; font-size: 0.875rem; }

.commit-row { padding: 8px 0; border-bottom: 1px solid #f0f0f0; }
.commit-row:last-child { border-bottom: none; }
.commit-top-row { display: flex; align-items: center; gap: 6px; margin-bottom: 3px; flex-wrap: wrap; }
.commit-sha-link {
  font-family: monospace;
  font-size: 0.75rem;
  background: #f3e5f5;
  color: #6a1b9a;
  padding: 1px 5px;
  border-radius: 3px;
  text-decoration: none;
}
.commit-sha-link:hover { background: #e1bee7; }
.commit-sha-text { font-family: monospace; font-size: 0.75rem; background: #f3e5f5; color: #6a1b9a; padding: 1px 5px; border-radius: 3px; }
.commit-repo-label { font-size: 0.72rem; color: var(--text-secondary, #6c7a9c); }
.commit-date-label { font-size: 0.72rem; color: #aaa; margin-left: auto; }
.commit-message-text { font-size: 0.8rem; color: var(--text-primary, #1a1a2e); padding-left: 2px; }

.error-state {
  text-align: center;
  padding: 64px 24px;
  color: var(--text-secondary, #6c7a9c);
}
.error-state i { font-size: 3rem; margin-bottom: 16px; color: #e53935; }
.error-state p { margin-bottom: 24px; }
</style>
